import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { ApiError } from "./api";

/** What the server said before anything happened: its confirm_required message and preview. */
export interface ServerPreview<P = Record<string, unknown>> {
  message: string;
  details: P;
}

/** Calls an operation without confirm and returns its preview. A server that runs it straight away (no confirm
 *  asked) would have changed something without the person seeing it, so that is reported as a problem. */
export async function askPreview<P>(send: (confirm: boolean) => Promise<unknown>): Promise<ServerPreview<P>> {
  try {
    await send(false);
  } catch (e) {
    if (e instanceof ApiError && e.code === "confirm_required") return { message: e.message, details: (e.details ?? {}) as P };
    throw e;
  }
  throw new Error("The server didn't ask for a confirmation first. Check the result on the page before trying again.");
}

/** The confirm_required message without the closing "show Brandon, get his OK" sentence written for AI readers. */
export function plainConfirmMessage(message: string): string {
  return message
    .replace(/\s*Show the details to Brandon, get his OK, then call again with "confirm": true\./i, "")
    .replace(/\s*From an API or MCP token, confirming does not run this:.*$/is, "")
    .trim();
}

/**
 * Preview-then-confirm for an operation that asks first (money ops and handler confirms).
 * review() calls without confirm and keeps the server's preview; confirm() sends the same input with confirm=true.
 */
export function usePreviewConfirm<P = Record<string, unknown>, R = unknown>(send: (confirm: boolean) => Promise<R>, onDone: (r: R) => void) {
  const [preview, setPreview] = useState<ServerPreview<P> | null>(null);
  const [problem, setProblem] = useState("");
  const review = useMutation({
    mutationFn: () => askPreview<P>(send),
    onMutate: () => setProblem(""),
    onSuccess: setPreview,
    onError: (e) => setProblem(e.message),
  });
  const confirm = useMutation({
    mutationFn: () => send(true),
    onMutate: () => setProblem(""),
    onSuccess: onDone,
    onError: (e) => {
      // The details changed between the preview and the confirm: show the new preview instead.
      if (e instanceof ApiError && e.code === "confirm_required") {
        setPreview({ message: e.message, details: (e.details ?? {}) as P });
        setProblem("Something changed since the preview. Check the new one below before confirming.");
        return;
      }
      setProblem(e.message);
    },
  });
  return {
    preview,
    problem,
    setProblem,
    back: () => setPreview(null),
    review: () => review.mutate(),
    confirm: () => confirm.mutate(),
    reviewing: review.isPending,
    confirming: confirm.isPending,
  };
}
